import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import BottomNav from '@/components/BottomNav';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Heart, MapPin, Briefcase, GraduationCap, Phone, ExternalLink, ArrowUpDown, Sun, Moon, Globe, User, Search, X, Filter, Star, Users, Sparkles, CheckCircle, ChevronDown, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { MARRIAGE_STATUS_OPTIONS, TUJUAN_OPTIONS } from '@/data/profileFields';
import { ExtendedProfileView, hasExtendedProfile } from '@/components/ExtendedProfileView';
import { ReportButton } from '@/components/ReportDialog';
import { TopLikedWidget } from '@/components/TopLikedWidget';
import MemberStatsSheet from '@/components/MemberStatsSheet';
import BrowseCoachmark, { COACH_KEY } from '@/components/BrowseCoachmark';
import axios from 'axios';
import { toast } from 'sonner';

export default function BrowsePage() {
  const navigate = useNavigate();
  const { token, member, claimedProfile, API } = useAuth();
  const { t, lang } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [filters, setFilters] = useState({ domiciles: [], countries: [], cities_by_country: {}, categories: [] });
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [category, setCategory] = useState('');
  const [gender, setGender] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [topLiked, setTopLiked] = useState([]);
  const [likeAnimId, setLikeAnimId] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);

  // Pagination — always 5 profiles per page so the grid stays fast & scannable
  const PAGE_SIZE = 8;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Reset to first page whenever any filter/sort changes
  useEffect(() => { setPage(1); }, [filterCountry, filterCity, category, gender, ageMin, ageMax, sortBy, sortOrder]);

  // Scroll to top when page changes so user immediately sees the new set of profiles
  useEffect(() => {
    if (page > 1) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  // First-visit filter coach-mark (auto opens; remembered via localStorage when "Yes I understand" checked)
  const [coachOpen, setCoachOpen] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem(COACH_KEY)) setCoachOpen(true);
  }, []);

  // Drag controls for swipe-down-to-close on profile detail modal (mobile-native UX).
  const dragControls = useDragControls();
  const closeProfileModal = () => { setSelectedProfile(null); setPhotoIdx(0); };

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCountry) params.append('country', filterCountry);
      if (filterCity) params.append('city', filterCity);
      if (category) params.append('category', category);
      if (gender) params.append('gender', gender);
      if (ageMin) params.append('age_min', ageMin);
      if (ageMax) params.append('age_max', ageMax);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('page', String(page));
      params.append('limit', String(PAGE_SIZE));
      const res = await axios.get(`${API}/members/browse?${params.toString()}`, { headers });
      setProfiles(res.data.profiles || []);
      setTotal(res.data.total || 0);
      setFilters(res.data.filters || { domiciles: [], countries: [], cities_by_country: {}, categories: [] });
    } catch (err) { console.error('[BrowsePage] fetchProfiles failed:', err); }
    setLoading(false);
  }, [API, headers, filterCountry, filterCity, category, gender, ageMin, ageMax, sortBy, sortOrder, page]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const url = category
        ? `${API}/members/suggestions?category=${encodeURIComponent(category)}`
        : `${API}/members/suggestions`;
      const res = await axios.get(url, { headers });
      setSuggestions(res.data.suggestions || []);
      setTopLiked(res.data.top_liked || []);
    } catch (err) { console.error('[BrowsePage] fetchSuggestions failed:', err); }
  }, [API, headers, category]);

  useEffect(() => { fetchProfiles(); fetchSuggestions(); }, [fetchProfiles, fetchSuggestions]);

  const getPhoto = (p) => p?.photos?.find(ph => ph) || '';
  const activeFilters = [filterCountry, filterCity, category, gender, ageMin, ageMax].filter(Boolean).length;

  // Prefer server-provided `countries` + `cities_by_country`; fall back to derivation
  // from legacy `domiciles` strings if the server hasn't been redeployed yet.
  const { countryOptions, citiesByCountry } = useMemo(() => {
    if (filters.countries && filters.countries.length > 0) {
      return {
        countryOptions: filters.countries,
        citiesByCountry: filters.cities_by_country || {},
      };
    }
    const byC = {};
    const countries = new Set();
    for (const dom of (filters.domiciles || [])) {
      if (!dom) continue;
      const parts = dom.split(' - ').map(s => s.trim()).filter(Boolean);
      if (parts.length === 0) continue;
      const c = parts[0];
      countries.add(c);
      const cityLabel = parts.slice(1).join(' - ');
      if (cityLabel) {
        if (!byC[c]) byC[c] = new Set();
        byC[c].add(cityLabel);
      }
    }
    const countryOpts = Array.from(countries).sort((a, b) => a.localeCompare(b));
    const cityMap = {};
    for (const c of countryOpts) {
      cityMap[c] = byC[c] ? Array.from(byC[c]).sort((a, b) => a.localeCompare(b)) : [];
    }
    return { countryOptions: countryOpts, citiesByCountry: cityMap };
  }, [filters.countries, filters.cities_by_country, filters.domiciles]);

  // When the country changes, invalidate city if it doesn't belong to the new country.
  useEffect(() => {
    if (filterCity && !(citiesByCountry[filterCountry] || []).includes(filterCity)) {
      setFilterCity('');
    }
  }, [filterCountry, filterCity, citiesByCountry]);

  const clearFilters = () => {
    setFilterCountry(''); setFilterCity('');
    setCategory(''); setGender(''); setAgeMin(''); setAgeMax('');
  };

  const getMarriageLabel = (val) => {
    const opt = MARRIAGE_STATUS_OPTIONS.find(o => o.value === val);
    return opt ? (lang === 'en' ? opt.en : opt.id) : val;
  };
  const getTujuanLabel = (val) => {
    const opt = TUJUAN_OPTIONS.find(o => o.value === val);
    return opt ? (lang === 'en' ? opt.en : opt.id) : val;
  };
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleLike = async (memberId, e) => {
    e?.stopPropagation();
    try {
      const res = await axios.post(`${API}/members/like`, { member_id: memberId }, { headers });
      if (res.data.liked) {
        setLikeAnimId(memberId);
        setTimeout(() => setLikeAnimId(null), 1200);
      }
      // Update profiles locally
      setProfiles(prev => prev.map(p => p.id === memberId ? { ...p, likes: res.data.likes, like_count: res.data.like_count } : p));
      setSuggestions(prev => prev.map(p => p.id === memberId ? { ...p, likes: res.data.likes, like_count: res.data.like_count } : p));
      setTopLiked(prev => prev.map(p => p.id === memberId ? { ...p, likes: res.data.likes, like_count: res.data.like_count } : p));
      if (selectedProfile?.id === memberId) {
        setSelectedProfile(prev => ({ ...prev, likes: res.data.likes, like_count: res.data.like_count }));
      }
    } catch (err) { toast.error('Failed'); }
  };

  const isLikedByMe = (profile) => {
    if (!member) return false;
    return (profile.likes || []).some(l => l.id === member.id);
  };

  const InfoRow = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-2.5 py-1.5">
        <Icon className="w-4 h-4 t-text-muted mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-[10px] t-text-muted uppercase tracking-wider">{label}</p>
          <p className="t-text-secondary text-sm">{value}</p>
        </div>
      </div>
    );
  };

  // Horizontal scroll card for suggestions/top liked
  const HorizCard = ({ p, showLikes }) => {
    const photo = getPhoto(p);
    return (
      <div onClick={() => { setSelectedProfile(p); setPhotoIdx(0); }} className="flex-shrink-0 w-[140px] cursor-pointer group">
        <div className="relative aspect-[3/4] rounded-2xl overflow-hidden glass-card">
          {photo ? <img src={photo} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center t-bg-input"><User className="w-8 h-8 t-text-muted" /></div>}
          {p.is_matched && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div className="bg-red-600/90 text-white font-black text-sm px-4 py-1.5 -rotate-[30deg] shadow-lg tracking-widest" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>MATCHED</div>
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2.5">
            <p className="text-white font-semibold text-xs truncate">{p.name || (lang === 'en' ? 'Unnamed' : 'Tanpa Nama')}</p>
            <p className="text-white/60 text-[10px]">{p.age}, {p.domicile?.split(' - ').pop()}</p>
          </div>
          {/* Like button overlay */}
          <button onClick={(e) => handleLike(p.id, e)} className="absolute top-2 left-2 p-1.5 rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/40 transition">
            <Heart className={`w-3.5 h-3.5 ${isLikedByMe(p) ? 'text-[#D98A94] fill-[#D98A94]' : 'text-white'}`} />
          </button>
        </div>
        {showLikes && p.like_count > 0 && (
          <div className="flex items-center gap-1 mt-1.5 px-1">
            <div className="flex -space-x-1.5">
              {(p.likes || []).slice(0, 3).map((l) => (
                <div key={l.id || l.name} className="w-4 h-4 rounded-full bg-[#D98A94]/20 text-[#D98A94] text-[7px] font-bold flex items-center justify-center border border-[var(--bg-main)]">{getInitials(l.name)}</div>
              ))}
            </div>
            <span className="text-[10px] t-text-muted">{p.like_count}</span>
          </div>
        )}
      </div>
    );
  };

  // ---- Claim-required gate: hide the entire directory until the member claims a profile ----
  if (!claimedProfile) {
    return (
      <div className="min-h-screen t-bg flex flex-col">
        <div className="sticky top-0 z-40 glass px-4 sm:px-6 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <img src="/images/mgi-logo.jpeg" alt="MGI" className="w-7 h-7 rounded-md object-contain" />
          <h1 className="text-lg sm:text-xl font-bold t-text">{t('member_directory')}</h1>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-3xl p-8 max-w-md text-center" data-testid="claim-required-gate">
            <div className="w-16 h-16 rounded-full bg-[#D98A94]/15 flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-[#D98A94]" />
            </div>
            <h2 className="text-xl font-bold t-text mb-2">
              {lang === 'en' ? 'Claim your profile first' : 'Klaim profil Anda dulu'}
            </h2>
            <p className="t-text-secondary text-sm mb-6">
              {lang === 'en'
                ? 'You need to claim your MGI profile before you can browse other members. Once claimed, you\'ll see members in your category (e.g. Buddhist Match members see other Buddhist Match members).'
                : 'Anda harus klaim profil MGI Anda dulu sebelum bisa melihat data member lain. Setelah klaim, Anda hanya akan melihat member di kategori Anda (mis. anggota Buddhist Match hanya melihat sesama Buddhist Match).'}
            </p>
            <button onClick={() => navigate('/profile')} data-testid="claim-gate-cta"
              className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white font-semibold px-6 py-3 rounded-full transition">
              {lang === 'en' ? 'Go to Claim Profile' : 'Ke Halaman Klaim Profil'}
            </button>
          </motion.div>
        </div>
        <BottomNav active="browse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen t-bg pb-24">
      <div className="max-w-md mx-auto">
      {/* Top bar */}
      <div className="sticky top-0 z-40 glass px-4 sm:px-6 py-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <img src="/images/mgi-logo.jpeg" alt="MGI" className="w-7 h-7 rounded-md object-contain" />
            <h1 className="text-lg sm:text-xl font-bold t-text">{t('member_directory')}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setStatsOpen(true)}
              data-testid="open-member-stats-btn"
              className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-2 rounded-full border border-[#D98A94]/30 bg-[#D98A94]/10 hover:bg-[#D98A94]/20 text-[#C0727C] text-xs font-bold transition-colors"
              title={lang === 'en' ? 'Deep Statistics' : 'Statistik Mendalam'}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{lang === 'en' ? 'Stats' : 'Statistik'}</span>
            </button>
            <button onClick={toggleTheme} data-testid="browse-theme-toggle" className="p-2 rounded-full glass t-text-secondary">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn" className={`p-2 rounded-full glass t-text-secondary relative ${showFilters ? 'text-[#D98A94]' : ''}`}>
              <Filter className="w-4 h-4" />
              {activeFilters > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D98A94] text-white text-[10px] rounded-full flex items-center justify-center">{activeFilters}</span>}
            </button>
          </div>
        </div>

        {/* Quick filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={category || 'all'} onValueChange={v => setCategory(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-category" className="w-[160px] glass t-text text-sm h-9 rounded-full pl-3 pr-2">
              <Heart className="w-3.5 h-3.5 mr-1.5 shrink-0 text-[#D98A94]" /><SelectValue placeholder={lang === 'en' ? 'Category' : 'Kategori'} />
            </SelectTrigger>
            <SelectContent className="glass-card t-text">
              <SelectItem value="all" className="t-text">{lang === 'en' ? 'All Categories' : 'Semua'}</SelectItem>
              {filters.categories.map(c => <SelectItem key={c} value={c} className="t-text">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCountry || 'all'} onValueChange={v => { setFilterCountry(v === 'all' ? '' : v); }}>
            <SelectTrigger data-testid="filter-country" className="w-[160px] glass t-text text-sm h-9 rounded-full pl-3 pr-2">
              <Globe className="w-3.5 h-3.5 mr-1.5 shrink-0 text-[#D98A94]" /><SelectValue placeholder={lang === 'en' ? 'Country' : 'Negara'} />
            </SelectTrigger>
            <SelectContent className="glass-card t-text">
              <SelectItem value="all" className="t-text">{lang === 'en' ? 'All Countries' : 'Semua Negara'}</SelectItem>
              {countryOptions.map(c => <SelectItem key={c} value={c} className="t-text">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCity || 'all'} onValueChange={v => setFilterCity(v === 'all' ? '' : v)} disabled={!filterCountry}>
            <SelectTrigger data-testid="filter-city" className={`w-[160px] glass t-text text-sm h-9 rounded-full pl-3 pr-2 ${!filterCountry ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <MapPin className="w-3.5 h-3.5 mr-1.5 shrink-0 text-[#D98A94]" /><SelectValue placeholder={filterCountry ? (lang === 'en' ? 'City' : 'Kota') : (lang === 'en' ? 'Pick country first' : 'Pilih negara dulu')} />
            </SelectTrigger>
            <SelectContent className="glass-card t-text">
              <SelectItem value="all" className="t-text">{lang === 'en' ? 'All Cities' : 'Semua Kota'}</SelectItem>
              {(citiesByCountry[filterCountry] || []).map(c => <SelectItem key={c} value={c} className="t-text">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={v => setSortBy(v)}>
            <SelectTrigger data-testid="sort-by" className="w-[130px] glass t-text text-sm h-9 rounded-full pl-3 pr-2">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 shrink-0 text-[#D98A94]" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="glass-card t-text">
              <SelectItem value="name" className="t-text">{lang === 'en' ? 'Name' : 'Nama'}</SelectItem>
              <SelectItem value="age" className="t-text">{lang === 'en' ? 'Age' : 'Usia'}</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')} data-testid="sort-order-toggle" className="h-9 px-4 rounded-full glass text-xs font-semibold t-text-secondary hover:text-[#D98A94] transition-colors">{sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</button>
          {activeFilters > 0 && <button onClick={clearFilters} data-testid="clear-filters-btn" className="h-9 px-4 rounded-full bg-[#D98A94]/10 text-[#D98A94] text-xs font-semibold flex items-center gap-1 hover:bg-[#D98A94]/15 transition-colors"><X className="w-3 h-3" />{lang === 'en' ? 'Clear' : 'Reset'}</button>}
        </div>

        {/* Gender segmented pill — ALWAYS visible so users can filter by Pria/Wanita instantly */}
        <div className="mt-2 inline-flex items-center rounded-full glass p-0.5" data-testid="gender-segmented" role="tablist">
          {[
            { val: '', label_en: 'All', label_id: 'Semua' },
            { val: 'male', label_en: 'Male', label_id: 'Pria' },
            { val: 'female', label_en: 'Female', label_id: 'Wanita' },
          ].map(opt => {
            const isActive = gender === opt.val;
            return (
              <button
                key={opt.val || 'all'}
                onClick={() => setGender(opt.val)}
                data-testid={`gender-chip-${opt.val || 'all'}`}
                role="tab"
                aria-selected={isActive}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  isActive
                    ? 'bg-[#D98A94] text-white shadow-sm'
                    : 't-text-secondary hover:t-text'
                }`}
              >
                {lang === 'en' ? opt.label_en : opt.label_id}
              </button>
            );
          })}
        </div>

        {showFilters && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-2 pt-2 flex flex-wrap gap-2" style={{ borderTop: '1px solid var(--border-color)' }}>
            <Input data-testid="filter-age-min" type="number" value={ageMin} onChange={e => setAgeMin(e.target.value)} placeholder={lang === 'en' ? 'Min Age' : 'Usia Min'} className="w-[100px] glass t-text text-sm h-8 rounded-full px-3" />
            <Input data-testid="filter-age-max" type="number" value={ageMax} onChange={e => setAgeMax(e.target.value)} placeholder={lang === 'en' ? 'Max Age' : 'Usia Maks'} className="w-[100px] glass t-text text-sm h-8 rounded-full px-3" />
          </motion.div>
        )}
      </div>

      {/* Claim banner */}
      {!claimedProfile && (
        <div className="px-4 sm:px-6 pt-3">
          <motion.button initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} onClick={() => navigate('/profile')} data-testid="claim-banner"
            className="w-full p-3 rounded-xl bg-gradient-to-r from-[#D98A94]/10 to-[#C7867A]/10 flex items-center gap-3 hover:from-[#D98A94]/15 hover:to-[#C7867A]/15 transition-all text-left" style={{ border: '1px solid var(--border-color)' }}>
            <div className="w-10 h-10 rounded-full bg-[#D98A94]/10 flex items-center justify-center flex-shrink-0"><Heart className="w-5 h-5 text-[#D98A94]" /></div>
            <div className="flex-1 min-w-0">
              <p className="t-text text-sm font-semibold">{lang === 'en' ? 'Claim your profile!' : 'Klaim profil Anda!'}</p>
              <p className="t-text-muted text-xs">{lang === 'en' ? 'Let others find you' : 'Biarkan orang lain menemukan Anda'}</p>
            </div>
          </motion.button>
        </div>
      )}

      {/* Results count */}
      <div className="px-4 sm:px-6 pt-4 pb-2 flex items-center justify-between">
        <p className="t-text-muted text-xs" data-testid="results-count">
          {lang === 'en'
            ? `${total} ${total === 1 ? 'profile' : 'profiles'}`
            : `${total} profil`}
          {total > 0 && (
            <span>
              {lang === 'en'
                ? ` — page ${page} of ${totalPages}`
                : ` — halaman ${page} dari ${totalPages}`}
            </span>
          )}
        </p>
      </div>

      {/* Profile Grid */}
      <div className="px-4 sm:px-6">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#D98A94] border-t-transparent rounded-full animate-spin" /></div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20"><Search className="w-16 h-16 mx-auto mb-6 t-text-muted" /><p className="t-text-secondary text-lg">{t('no_profiles')}</p></div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {profiles.map((p, i) => {
              const photo = getPhoto(p);
              return (
                <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                  onClick={() => { setSelectedProfile(p); setPhotoIdx(0); }} data-testid={`profile-card-${p.id}`}
                  className="glass-card rounded-2xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform relative">
                  <div className="aspect-[3/4] bg-[var(--bg-input)] relative">
                    {photo ? <img src={photo} alt={p.name || ''} className="absolute inset-0 w-full h-full object-cover object-center" loading="lazy" /> : <div className="absolute inset-0 w-full h-full flex items-center justify-center"><User className="w-12 h-12 t-text-muted" /></div>}
                    {p.is_matched && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                        <div className="bg-red-600/90 text-white font-black text-base px-6 py-2 -rotate-[30deg] shadow-lg tracking-widest" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>MATCHED</div>
                      </div>
                    )}
                    {/* Like button */}
                    <button onClick={(e) => handleLike(p.id, e)} data-testid={`like-btn-${p.id}`}
                      className="absolute top-2 left-2 p-1.5 rounded-full bg-black/20 backdrop-blur-sm hover:bg-black/40 transition">
                      <Heart className={`w-4 h-4 ${isLikedByMe(p) ? 'text-[#D98A94] fill-[#D98A94]' : 'text-white'}`} />
                    </button>
                    {/* Like animation */}
                    <AnimatePresence>
                      {likeAnimId === p.id && (
                        <motion.div initial={{ scale: 0, opacity: 1 }} animate={{ scale: 2.5, opacity: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}
                          className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <Heart className="w-12 h-12 text-[#D98A94] fill-[#D98A94]" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                      <p className="text-white font-semibold text-sm truncate">{p.name || (lang === 'en' ? 'Unnamed' : 'Tanpa Nama')}</p>
                      <p className="text-white/60 text-xs">{p.age} {p.gender === 'male' ? 'M' : 'F'} - {p.domicile?.split(' - ').pop()}</p>
                    </div>
                  </div>
                  <div className="p-2.5">
                    <div className="flex flex-wrap gap-1 mb-1">
                      <span className="text-[10px] bg-[#D98A94]/10 text-[#D98A94] px-2 py-0.5 rounded-full">{p.category}</span>
                      {p.mbti && <span className="text-[10px] bg-[#C7867A]/10 text-[#C7867A] px-2 py-0.5 rounded-full">{p.mbti}</span>}
                    </div>
                    {/* Likes info */}
                    {p.like_count > 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <div className="flex -space-x-1">
                          {(p.likes || []).slice(0, 3).map((l) => (
                            <div key={l.id || l.name} className="w-4 h-4 rounded-full bg-[#D98A94]/15 text-[#D98A94] text-[7px] font-bold flex items-center justify-center border border-[var(--bg-main)]">{getInitials(l.name)}</div>
                          ))}
                        </div>
                        <span className="text-[10px] t-text-muted">{lang === 'en' ? `${p.like_count} liked` : `${p.like_count} suka`}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination controls — always visible when there is more than 1 page */}
        {!loading && total > 0 && totalPages > 1 && (
          <div className="mt-6 mb-4 flex flex-col items-center gap-3" data-testid="pagination-controls">
            <div className="flex items-center gap-2 flex-wrap justify-center">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="pagination-prev"
                className="h-9 px-4 rounded-full glass-card t-text text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#D98A94]/10 transition"
              >
                {lang === 'en' ? '‹ Prev' : '‹ Sebelumnya'}
              </button>

              {/* Numbered page buttons — show up to 7 pages with ellipsis */}
              <div className="flex items-center gap-1">
                {(() => {
                  const pages = [];
                  const maxShown = 7;
                  let start = Math.max(1, page - 3);
                  let end = Math.min(totalPages, start + maxShown - 1);
                  if (end - start < maxShown - 1) start = Math.max(1, end - maxShown + 1);
                  if (start > 1) {
                    pages.push(
                      <button key="p-1" onClick={() => setPage(1)} data-testid="pagination-page-1"
                        className="min-w-[36px] h-9 px-3 rounded-full glass t-text-secondary text-sm hover:bg-[#D98A94]/10">1</button>
                    );
                    if (start > 2) pages.push(<span key="lead-ellipsis" className="t-text-muted px-1">…</span>);
                  }
                  for (let i = start; i <= end; i++) {
                    const active = i === page;
                    pages.push(
                      <button
                        key={`p-${i}`}
                        onClick={() => setPage(i)}
                        data-testid={`pagination-page-${i}`}
                        aria-current={active ? 'page' : undefined}
                        className={`min-w-[36px] h-9 px-3 rounded-full text-sm font-semibold transition ${
                          active
                            ? 'bg-[#D98A94] text-white shadow'
                            : 'glass t-text-secondary hover:bg-[#D98A94]/10'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  if (end < totalPages) {
                    if (end < totalPages - 1) pages.push(<span key="trail-ellipsis" className="t-text-muted px-1">…</span>);
                    pages.push(
                      <button key={`p-${totalPages}`} onClick={() => setPage(totalPages)} data-testid={`pagination-page-${totalPages}`}
                        className="min-w-[36px] h-9 px-3 rounded-full glass t-text-secondary text-sm hover:bg-[#D98A94]/10">{totalPages}</button>
                    );
                  }
                  return pages;
                })()}
              </div>

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="pagination-next"
                className="h-9 px-4 rounded-full glass-card t-text text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#D98A94]/10 transition"
              >
                {lang === 'en' ? 'Next ›' : 'Selanjutnya ›'}
              </button>
            </div>
            <p className="t-text-muted text-[11px]" data-testid="pagination-label">
              {lang === 'en'
                ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`
                : `Menampilkan ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} dari ${total}`}
            </p>
          </div>
        )}
      </div>

      {/* Profile Detail Modal */}
      <Dialog open={!!selectedProfile} onOpenChange={(o) => { if (!o) closeProfileModal(); }}>
        <DialogContent className="glass-card t-text max-w-lg max-h-[90vh] overflow-y-auto p-0 [&>button.absolute]:hidden">
          {selectedProfile && (() => {
            const validPhotos = selectedProfile.photos?.filter(p => p) || [];
            return (
              <motion.div
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.6 }}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 120 || info.velocity.y > 600) closeProfileModal();
                }}
                className="relative"
              >
                {/* Big close button — easier to tap on mobile (pill with icon + text)
                    Wrapped in div so the parent [&>button.absolute]:hidden override doesn't hide this button. */}
                <div className="absolute top-3 right-3 z-40">
                  <button
                    onClick={closeProfileModal}
                    data-testid="modal-close-btn"
                    aria-label={lang === 'en' ? 'Close' : 'Tutup'}
                    className="flex items-center gap-1.5 pl-3 pr-4 py-2.5 rounded-full bg-white text-[#D98A94] shadow-2xl border-2 border-[#D98A94] hover:bg-[#D98A94] hover:text-white active:scale-95 transition font-bold text-sm"
                  >
                    <X className="w-5 h-5" strokeWidth={3} />
                    <span>{lang === 'en' ? 'Close' : 'Tutup'}</span>
                  </button>
                </div>

                {/* Photo area — uses object-contain so portraits are NEVER cropped.
                    Drag-to-close gesture is now activated ONLY from the small pill handle at the top
                    (a 80x28 px hit zone). The rest of the photo area allows normal vertical scroll. */}
                <div
                  className="relative w-full bg-black flex items-center justify-center"
                  style={{ minHeight: '320px', maxHeight: '60vh' }}
                  data-testid="modal-photo-area"
                >
                  {/* Drag-to-close handle (the only element with touchAction:none) */}
                  <div
                    onPointerDown={(e) => dragControls.start(e)}
                    style={{ touchAction: 'none' }}
                    data-testid="modal-drag-handle"
                    aria-label={lang === 'en' ? 'Drag down to close' : 'Geser ke bawah untuk tutup'}
                    className="absolute top-0 left-1/2 -translate-x-1/2 z-30 w-20 h-7 flex items-center justify-center cursor-grab active:cursor-grabbing"
                  >
                    <div className="w-10 h-1.5 rounded-full bg-white/80 shadow" />
                  </div>
                  {validPhotos.length > 0 ? (
                    <>
                      <img
                        src={validPhotos[photoIdx] || validPhotos[0]}
                        alt=""
                        className="w-full h-auto object-contain pointer-events-none select-none"
                        draggable={false}
                        style={{ maxHeight: '60vh' }}
                      />
                      {validPhotos.length > 1 && (
                        <>
                          <div className="absolute top-9 left-0 right-0 flex justify-center gap-1.5 z-10 pointer-events-none">
                            {validPhotos.map((photo, i) => <button key={`${photo.slice(-20)}-${i}`} onClick={(e) => { e.stopPropagation(); setPhotoIdx(i); }} className={`h-1 rounded-full transition-all pointer-events-auto ${i === photoIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/40'}`} />)}
                          </div>
                          <button onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => i > 0 ? i - 1 : validPhotos.length - 1); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 z-10 text-lg">&lsaquo;</button>
                          <button onClick={(e) => { e.stopPropagation(); setPhotoIdx(i => i < validPhotos.length - 1 ? i + 1 : 0); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 z-10 text-lg">&rsaquo;</button>
                        </>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent p-5 pointer-events-none">
                        <h2 className="text-white text-xl font-bold drop-shadow">{selectedProfile.name || 'Member'}, {selectedProfile.age}</h2>
                        <p className="text-white/80 text-sm drop-shadow">{selectedProfile.domicile}</p>
                      </div>
                      {selectedProfile.is_matched && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                          <div className="bg-red-600/90 text-white font-black text-2xl px-10 py-3 -rotate-[25deg] shadow-xl tracking-widest border-4 border-white/30" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>MATCHED</div>
                        </div>
                      )}
                    </>
                  ) : <div className="w-full h-80 flex items-center justify-center"><User className="w-20 h-20 t-text-muted" /></div>}
                </div>

                {/* Scroll hint — bouncing pill telling users to scroll for WhatsApp/IG buttons */}
                <div className="relative">
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 z-20 pointer-events-none flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D98A94] text-white text-[11px] font-bold shadow-lg animate-bounce">
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={3} />
                    <span>{lang === 'en' ? 'Scroll down for contact' : 'Geser ke bawah untuk kontak'}</span>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  {/* Like button + count */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-[#D98A94]/10 text-[#D98A94] px-3 py-1 rounded-full">{selectedProfile.category}</span>
                      {selectedProfile.mbti && <span className="text-xs bg-[#C7867A]/10 text-[#C7867A] px-3 py-1 rounded-full">{selectedProfile.mbti}</span>}
                      {selectedProfile.marriage_status && <span className="text-xs bg-purple-500/10 text-purple-400 px-3 py-1 rounded-full">{getMarriageLabel(selectedProfile.marriage_status)}</span>}
                    </div>
                    <button onClick={(e) => handleLike(selectedProfile.id, e)} data-testid="modal-like-btn"
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition ${isLikedByMe(selectedProfile) ? 'bg-[#D98A94] text-white' : 'glass t-text-secondary hover:bg-[#D98A94]/10'}`}>
                      <Heart className={`w-4 h-4 ${isLikedByMe(selectedProfile) ? 'fill-white' : ''}`} />
                      {selectedProfile.like_count || 0}
                    </button>
                  </div>

                  {/* Liked by */}
                  {selectedProfile.like_count > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2">
                        {(selectedProfile.likes || []).slice(0, 5).map((l) => (
                          <div key={l.id || l.name} className="w-6 h-6 rounded-full bg-[#D98A94]/15 text-[#D98A94] text-[8px] font-bold flex items-center justify-center border-2 border-[var(--bg-main)]">{getInitials(l.name)}</div>
                        ))}
                      </div>
                      <span className="t-text-muted text-xs">
                        {lang === 'en' ? `Liked by ${(selectedProfile.likes || [])[0]?.name || ''}${selectedProfile.like_count > 1 ? ` +${selectedProfile.like_count - 1} others` : ''}` : `Disukai ${(selectedProfile.likes || [])[0]?.name || ''}${selectedProfile.like_count > 1 ? ` +${selectedProfile.like_count - 1} lainnya` : ''}`}
                      </span>
                    </div>
                  )}

                  {selectedProfile.bio && <p className="t-text-secondary text-sm">{selectedProfile.bio}</p>}

                  <div className="glass-card rounded-xl p-4 space-y-1">
                    <InfoRow icon={Briefcase} label={t('occupation')} value={selectedProfile.occupation} />
                    <InfoRow icon={GraduationCap} label={t('education')} value={selectedProfile.education} />
                    <InfoRow icon={MapPin} label={t('domicile')} value={selectedProfile.domicile} />
                  </div>

                  {(selectedProfile.mbti || selectedProfile.zodiac || selectedProfile.shio || selectedProfile.aliran_agama || selectedProfile.hobby || selectedProfile.preferred_places || selectedProfile.topics_to_talk || selectedProfile.income_range) && (
                    <div className="glass-card rounded-xl p-4 space-y-1">
                      <InfoRow icon={Sparkles} label="MBTI" value={selectedProfile.mbti} />
                      <InfoRow icon={Star} label={lang === 'en' ? 'Zodiac' : 'Zodiak'} value={selectedProfile.zodiac} />
                      <InfoRow icon={Star} label="Shio" value={selectedProfile.shio} />
                      <InfoRow icon={Heart} label={lang === 'en' ? 'Religious Sect' : 'Aliran Agama'} value={selectedProfile.aliran_agama} />
                      <InfoRow icon={Heart} label="Hobby" value={selectedProfile.hobby} />
                      <InfoRow icon={MapPin} label={lang === 'en' ? 'Preferred Places' : 'Tempat Favorit'} value={selectedProfile.preferred_places} />
                      <InfoRow icon={Briefcase} label={lang === 'en' ? 'Monthly Income' : 'Pendapatan Bulanan'} value={selectedProfile.income_range} />
                    </div>
                  )}

                  {selectedProfile.kriteria_pasangan && (
                    <div className="glass-card rounded-xl p-4">
                      <p className="text-[10px] t-text-muted uppercase tracking-wider mb-1">{lang === 'en' ? 'Partner Criteria' : 'Kriteria Pasangan'}</p>
                      <p className="t-text-secondary text-sm">{selectedProfile.kriteria_pasangan}</p>
                    </div>
                  )}

                  {/* Extended optional profile (Batch 3) — read-only */}
                  {hasExtendedProfile(selectedProfile) && (
                    <div className="glass-card rounded-xl p-4">
                      <ExtendedProfileView profile={selectedProfile} lang={lang} />
                    </div>
                  )}

                  {/* Social Links — hidden when matched */}
                  {selectedProfile.is_matched ? (
                    <div className="glass-card rounded-xl p-4 text-center">
                      <CheckCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                      <p className="t-text font-semibold text-sm">{lang === 'en' ? 'This person has been matched!' : 'Orang ini telah berjodoh!'}</p>
                      <p className="t-text-muted text-xs mt-1">{lang === 'en' ? 'They are committed with another member and cannot be contacted.' : 'Mereka sudah berkomitmen dengan anggota lain dan tidak dapat dihubungi.'}</p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedProfile.whatsapp && (
                        <a href={`https://wa.me/${selectedProfile.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" data-testid="profile-wa-link"
                          className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-500/10 text-green-500 hover:bg-green-500/20 transition text-sm font-medium">
                          <Phone className="w-4 h-4" />WhatsApp
                        </a>
                      )}
                      {selectedProfile.instagram && (
                        <a href={`https://instagram.com/${selectedProfile.instagram}`} target="_blank" rel="noopener noreferrer" data-testid="profile-ig-link"
                          className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 transition text-sm font-medium">
                          <ExternalLink className="w-4 h-4" />Instagram
                        </a>
                      )}
                      {selectedProfile.tiktok && (
                        <a href={`https://tiktok.com/@${selectedProfile.tiktok}`} target="_blank" rel="noopener noreferrer" data-testid="profile-tt-link"
                          className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-xl bg-black/10 t-text hover:bg-black/20 transition text-sm font-medium">
                          <ExternalLink className="w-4 h-4" />TikTok
                        </a>
                      )}
                      {selectedProfile.facebook && (
                        <a href={selectedProfile.facebook.startsWith('http') ? selectedProfile.facebook : `https://facebook.com/${selectedProfile.facebook}`} target="_blank" rel="noopener noreferrer" data-testid="profile-fb-link"
                          className="flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600/10 text-blue-500 hover:bg-blue-600/20 transition text-sm font-medium">
                          <ExternalLink className="w-4 h-4" />Facebook
                        </a>
                      )}
                    </div>
                  )}

                  {/* Helper note about social links */}
                  {!selectedProfile.is_matched && (selectedProfile.whatsapp || selectedProfile.instagram || selectedProfile.tiktok || selectedProfile.facebook) && (
                    <div className="px-3 py-2 rounded-lg bg-[#F59E0B]/8 border border-[#F59E0B]/20 flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                      <p className="t-text-secondary text-[11px] leading-snug">
                        {lang === 'en'
                          ? <>Tapping the <b>WhatsApp</b> / <b>Instagram</b> / <b>TikTok</b> / <b>Facebook</b> button opens that member's profile directly in the app. If it opens to an empty/missing page, the member entered the wrong username/number — just move on to another profile.</>
                          : <>Tombol <b>WhatsApp</b> / <b>Instagram</b> / <b>TikTok</b> / <b>Facebook</b> akan langsung membuka profil anggota tersebut di aplikasi. Jika halaman terbuka kosong/tidak ditemukan, berarti anggota tersebut salah memasukkan username/nomor — silakan lanjut ke profil lain saja.</>}
                      </p>
                    </div>
                  )}

                  {/* Report button */}
                  <ReportButton profile={selectedProfile} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs t-text-muted hover:text-red-500 hover:bg-red-500/5 transition" />
                </div>
              </motion.div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <BrowseCoachmark open={coachOpen} onClose={() => setCoachOpen(false)} lang={lang} />

      <MemberStatsSheet
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        category={claimedProfile?.category}
        lang={lang}
      />

      </div>
      <BottomNav active="browse" />
    </div>
  );
}
